import { action, computed, makeObservable } from 'mobx';

import { timelogsApi } from '@/api/timelogsApi';
import { yandexTrackerApi } from '@/api/yandexTrackerApi';
import { UserStore } from '@/configs/store';
import { AlertsStore } from '@/configs/store/AlertsStore';
import { FavoriteUsersModel } from '@/features/favoriteUsers/FavoriteUsersModel';
import { LogsMode } from '@/features/logsMode';
import { UsersResponse, YandexUser } from '@/types/yandex';
import { formatError } from '@/utils/formatError';
import { transformTimelogsUser } from '@/utils/transformTimelog';

import { LoadingStageModel } from './LoadingStageModel';
import { SuggestionsModel } from './SuggestionsModel';
import { ToggleModel } from './ToggleModel';

export interface IUserSuggestModel {
  suggestions: YandexUser[];
  suggestQuery: string;
  loadingStage: LoadingStageModel;
  showAll: boolean;
  options: YandexUser[];

  setSuggestQuery: (query: string) => void;
  toggleShowAll: () => void;
}

/**
 * Модель для поиска и подсказок пользователей.
 * Чистая модель без логики выбора пользователя, URL-параметров и localStorage.
 */
export class UserSuggestModel implements IUserSuggestModel {
  private _suggestionsModel: SuggestionsModel<YandexUser>;
  private readonly _loadingStage = new LoadingStageModel();
  private _showAll = new ToggleModel();
  readonly favoriteUsersModel: FavoriteUsersModel;

  protected readonly userStore: UserStore;
  protected readonly alertsStore: AlertsStore;
  private readonly _mode: LogsMode;

  constructor(
    userStore: UserStore,
    alertsStore: AlertsStore,
    favoriteUsersModel: FavoriteUsersModel,
    mode: LogsMode = LogsMode.legacy,
  ) {
    this._mode = mode;
    this.favoriteUsersModel = favoriteUsersModel;
    this.userStore = userStore;
    this.alertsStore = alertsStore;

    makeObservable(this, {
      suggestions: computed,
      suggestQuery: computed,
      options: computed,
      loadingStage: computed,
      showAll: computed,
      setSuggestQuery: action,
      toggleShowAll: action,
    });

    this._suggestionsModel = new SuggestionsModel<YandexUser>({
      fetchFn: async ({ query, signal }) => {
        if (!this.userStore.isAuthorized) return [];

        if (this._mode === LogsMode.v2) {
          const users = await timelogsApi.suggestUsers({
            query,
            includeExternal: this._showAll.value,
            signal,
          });

          return users.map(transformTimelogsUser);
        }

        const response: UsersResponse = await yandexTrackerApi.getUsers({
          q: query,
          showAll: this._showAll.value,
          signal,
        });

        if ('details' in response) {
          const error = formatError(response);

          this.alertsStore.addAlert({
            message: error.message,
            title: error.title,
            withReload: false,
            autoClose: true,
          });

          return [];
        }

        return response.items.map((u) => ({
          uid: u.id,
          display: u.name,
          email: u.email,
        }));
      },
    });
  }

  get loadingStage() {
    return this._loadingStage;
  }

  get suggestions() {
    return this._suggestionsModel.suggestions;
  }

  get suggestQuery() {
    return this._suggestionsModel.suggestQuery;
  }

  get showAll() {
    return this._showAll.value;
  }

  /**
   * Возвращает список опций для выбора.
   * Избранные пользователи показываются первыми, если поисковый запрос пуст.
   */
  get options(): YandexUser[] {
    const isQueryEmpty = this.suggestQuery.trim() === '';

    if (isQueryEmpty) {
      const favoriteUsersList = this.favoriteUsersModel.favoriteUsers;
      const regularUsers = this.suggestions.filter(
        (u) => !this.favoriteUsersModel.isFavorite(u.uid),
      );

      return [...favoriteUsersList, ...regularUsers];
    }

    return this.suggestions;
  }

  toggleShowAll = () => {
    this._showAll.toggle();
    this._suggestionsModel.refresh();
  };

  setSuggestQuery = (query: string) => {
    this._suggestionsModel.setSuggestQuery(query);
  };

  loadUserDetail = async (id: string): Promise<YandexUser> => {
    this._loadingStage.loading();

    if (!this.userStore.isAuthorized) {
      this._loadingStage.error();

      throw new Error('Not authorized');
    }

    try {
      const user =
        this._mode === LogsMode.v2
          ? transformTimelogsUser(await timelogsApi.getUser(id))
          : await yandexTrackerApi.getUserById(id);
      this._loadingStage.success();

      return user;
    } catch (e) {
      this._loadingStage.error();
      console.error('Failed to load user detail:', e);

      const error = formatError(e);

      this.alertsStore.addAlert({
        message: error.message,
        title: error.title,
        withReload: false,
        autoClose: true,
      });

      throw e;
    }
  };
}

// Для обратной совместимости
export { UserSuggestModel as UserAsyncSelectorModel };
export type { IUserSuggestModel as IUserAsyncSelectorModel };
